import { Image } from 'antd';
import styled from 'styled-components';
import Carousel from "react-multi-carousel";
import { EyeOutlined } from '@ant-design/icons';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import "react-multi-carousel/lib/styles.css";

const responsive = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1, slidesToSlide: 1 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 1, slidesToSlide: 1 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 }
};

const Style = styled.div`
  position: relative;

  .bottom {
    position: absolute;
    bottom: 22px;
    left: 0px;
    z-index: 1000;
    color: white;
    width: 100%;
    background: linear-gradient(0deg, #000000 18.23%, rgba(0, 0, 0, 0) 100%);

    .controller { 
      display: flex;
      justify-content: flex-end;
      padding: 4px 22px;
      gap: 4px;

      .cont {
        display: flex;
        flex-direction: column;
        justify-content: center;

        span {
          margin-right: 4px;
          font-weight: 600;
        }
      }

      .dot {
        width: 10px;
        height: 10px;
        background: white;
        cursor: pointer;
        border-radius: 100%;

        &.dot-active { 
          background: var(--color-primary-70);
        }
      }
    }
  }

  .ant-carousel .slick-dots-bottom {
    display: none !important;
  }

  .image-cont {
    position: relative;

    .skin {
      opacity: 0;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
      color: white;
      position: absolute;
      display: flex;
      flex-direction: column;
      cursor: pointer;
      transition: .4s opacity;

      .preview {
        flex: 1;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        justify-content: center;

        div {
          display: flex;
          justify-content: center;
          gap: 4px;

          svg {
            margin-top: 3px;
          }

          label {
            color: white;
            margin-bottom: 0px;
            cursor: pointer;
          }
        }
      }
    }

    &:hover {
      .skin {
        opacity: 1;
      } 
    }

    .image {
      width: 100%;
      height: 230px;
      background-position: center;
      background-size: cover;
    }
  }

  &.amp-overview {
    .image-cont .image {
      height: 170px;
    }
  }
`;

const InformationBy = styled.div`
  padding: 0px 14px;
`;

const CustomImage = ({ src }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(false);

  return (
    <>
      <div className="image-cont">
        <div style={{ backgroundImage: `url('${src}')` }} className="image" />
        <div className="skin" onClick={() => setPreview(true)}>
          <div className="preview">
            <div>
              <EyeOutlined />
              <label>{t('Preview')}</label>
            </div>
          </div>
        </div>
      </div>
      <Image
        preview={{ src, visible: preview, onVisibleChange: (v) => setPreview(v) }}
      />
    </>
  );
}

const Gallery = ({ images, className = '' }) => {
  const ref = useRef();
  const [slide, setSlide] = useState(0);

  const goTo = (slide) => {
    ref.current.goToSlide(slide, false);
  };

  const urls = useMemo(() => {
    if (images && Array.isArray(images)) {
      return images.map((f) => {
        if (f.url) {
          return {
            url: f.url,
            sourceName: f.sourceName,
          };
        }
        return {};
      });
    }
    return null;
  }, [images]);

  const afterChange = (prev, current) => {
    setSlide(current.currentSlide);
  };

  const dots = useMemo(() => {
    const items = [];
    for (let i = 0; i < urls.length; i++) {
      items.push(`${i}`);
    }
    return items;
  }, [urls]);


  return urls && Array.isArray(urls) && urls.length ? (
    <>
      <Style className={className}>
        <div className="bottom">
          <div className="controller">
            <div className="cont">
              <span>{slide + 1}/{urls.length}</span>
            </div>
            {dots.map((d, i) => (
              <div
                className="cont"
                key={`dots-${d}`}
              >
                <div
                  className={slide === i ? 'dot dot-active' : 'dot'}
                  onClick={() => goTo(i)}
                />
              </div>
            ))}
          </div>
        </div>
        <Carousel
          ref={ref}
          swipeable
          showDots={false}
          responsive={responsive}
          afterChange={afterChange}
        >
          {urls.map((img, i) => (
            <div key={i.toString()}>
              <CustomImage src={img.url} />
            </div>
          ))}
        </Carousel>
      </Style>
      {(urls[slide] && urls[slide].sourceName) && (
        <InformationBy>
          <label>&#169; Image by {urls[slide].sourceName}</label>
        </InformationBy>
      )}
    </>
  ) : null;
};

export default Gallery;
